import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { fetchWorkspaceConfigData } from "../api/workspaceConfigApi";

// Per-workspace presentation config (UI layout presets, combo-metric labels,
// the export flag), keyed by workspace id and fetched when a workspace is
// selected. Never persisted: the server owns it, and a stale local copy would
// win over a changed config on the next load.

/**
 * Fetch one workspace's config. Skips the request when that workspace's
 * config is in flight, or loaded at the live revision token (any loaded entry
 * counts while no live token is known). An entry read at an older revision
 * refetches, so a revision bump reaches every cached workspace, not only the
 * active one. Pass `force: true` to refetch regardless.
 */
export const fetchWorkspaceConfig = createAsyncThunk(
  "workspaceConfig/fetch",
  /** @param {{ idClient: string; force?: boolean }} arg */
  async ({ idClient }, { rejectWithValue }) => {
    try {
      return await fetchWorkspaceConfigData(idClient);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load workspace config");
    }
  },
  {
    condition: ({ idClient, force }, { getState }) => {
      if (!idClient) return false;
      if (force) return true;
      const { byId, liveConfigVersion } = getState().workspaceConfig ?? {};
      const entry = byId?.[idClient];
      if (entry?.status === "loading") return false;
      if (entry?.status !== "ready") return true;
      return liveConfigVersion != null && entry.config?.config_version !== liveConfigVersion;
    },
  }
);

const initialState = {
  // { [idClient]: { status: "loading" | "ready" | "error", config } }
  byId: {},
  // The global config revision token last seen live, or null when unknown.
  liveConfigVersion: null,
};

const workspaceConfigSlice = createSlice({
  name: "workspaceConfig",
  initialState,
  reducers: {
    liveConfigVersionChanged: (state, action) => {
      state.liveConfigVersion = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceConfig.pending, (state, action) => {
        const { idClient } = action.meta.arg;
        const previous = state.byId[idClient];
        // Keep the previous config while a refetch is in flight, so a
        // revision refresh never flashes the default layout.
        state.byId[idClient] = { status: "loading", config: previous?.config ?? null };
      })
      .addCase(fetchWorkspaceConfig.fulfilled, (state, action) => {
        state.byId[action.meta.arg.idClient] = {
          status: "ready",
          config: action.payload,
        };
      })
      .addCase(fetchWorkspaceConfig.rejected, (state, action) => {
        if (action.meta.condition) return;
        const { idClient } = action.meta.arg;
        state.byId[idClient] = {
          status: "error",
          config: state.byId[idClient]?.config ?? null,
        };
      });
  },
});

export const { liveConfigVersionChanged } = workspaceConfigSlice.actions;

/** The loaded config for a workspace, or null when none has loaded. */
export const selectWorkspaceConfig = (state, idClient) =>
  (idClient && state.workspaceConfig?.byId?.[idClient]?.config) || null;

/** The loaded config for the workspace the session's data is scoped to. */
export const selectActiveWorkspaceConfig = (state) =>
  selectWorkspaceConfig(state, state.auth?.id_client);

/**
 * Whether the active workspace's config has settled, loaded or failed. The
 * boot gate waits on it so a dashboard never renders the default layout first.
 */
export const selectActiveWorkspaceConfigSettled = (state) => {
  const idClient = state.auth?.id_client;
  if (!idClient) return true;
  const status = state.workspaceConfig?.byId?.[idClient]?.status;
  return status === "ready" || status === "error";
};

export default workspaceConfigSlice.reducer;
